import { IStairConfig, IWoodMaterial } from '@stair-craft/common/util';

import { IRailingVolume, IRailingWoodEstimate, railingWoodEstimate, woodMaterial } from '../shared-railing-wood/railing-wood';

/** Offcut allowance on every part: the wood bought is 15% more than the wood in the stair. */
export const WOOD_WASTE_FACTOR: number = 1.15;

/** Stair wood volume in m³ before waste, split by the species each part is cut from. */
export interface IStairWoodVolume {
    /** Treads, winder treads and landing boards; a spiral's sector steps. */
    readonly tread: number;
    /** Riser boards. */
    readonly riser: number;
    /** The load-bearing frame: stringers (kosour or tetiva), a spiral's central pole. */
    readonly stringer: number;
    /** Handrail and balusters with posts, each with a species of its own. */
    readonly railing: IRailingVolume;
}

/** The figures a calculator puts on its result, before rounding. */
export interface IStairWoodEstimate {
    readonly woodVolumeM3: number;
    readonly estimatedWeightKg: number;
    readonly estimatedCostUsd: number;
}

/**
 * Volume, weight and cost of the whole stair. Each part is weighed and priced by its own
 * species: treads by `treadMaterialId`, risers by `riserMaterialId`, the frame by
 * `stringerMaterialId`, the railing by its two species in `railingWoodEstimate`.
 */
export function stairWoodEstimate(volume: IStairWoodVolume, config: IStairConfig): IStairWoodEstimate {
    const parts: readonly (readonly [number, IWoodMaterial])[] = [
        [volume.tread * WOOD_WASTE_FACTOR, woodMaterial(config.treadMaterialId)],
        // An open stair has no riser boards: they neither weigh nor cost.
        [config.risersEnabled ? volume.riser * WOOD_WASTE_FACTOR : 0, woodMaterial(config.riserMaterialId)],
        [volume.stringer * WOOD_WASTE_FACTOR, woodMaterial(config.stringerMaterialId)],
    ];
    const railingWood: IRailingWoodEstimate = railingWoodEstimate(volume.railing, config, WOOD_WASTE_FACTOR);

    let woodVolumeM3: number = (volume.railing.handrail + volume.railing.baluster) * WOOD_WASTE_FACTOR;
    let estimatedWeightKg: number = railingWood.weightKg;
    let estimatedCostUsd: number = railingWood.costUsd;
    for (const [m3, material] of parts) {
        woodVolumeM3 += m3;
        estimatedWeightKg += m3 * material.density;
        estimatedCostUsd += m3 * material.pricePerM3;
    }
    return { woodVolumeM3, estimatedWeightKg, estimatedCostUsd };
}
